import React, { useState, useMemo } from "react";
import { C } from "../lib/theme";
import { Check, X, GripVertical } from "lucide-react";

// Symbol units ("%", "°C") hug their number; word units ("dollars", "years")
// need a space or the slider reads as "110dollars".
const withUnit = (v, unit) => {
  const u = (unit || "").trim();
  return `${v}${u && /^[a-z]/i.test(u) ? " " : ""}${u}`;
};

function Result({ correct, text }) {
  return (
    <div
      className="mt-3 flex items-start gap-2 rounded-md border p-3 text-sm anim-fade-up"
      style={{ borderColor: correct ? C.grn : C.ros, background: `${correct ? C.grn : C.ros}14` }}
      data-testid="check-result"
    >
      {correct ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-grn" /> : <X className="mt-0.5 h-4 w-4 shrink-0 text-ros" />}
      <span className="text-ink/90">{text}</span>
    </div>
  );
}

// One interactive check. Calls onResult(correct) once answered.
export default function CheckWidget({ check, onResult }) {
  const [done, setDone] = useState(false);
  const [correct, setCorrect] = useState(false);
  const [picked, setPicked] = useState(null);
  const [flash, setFlash] = useState("");

  const finish = (ok, choice = null) => {
    if (done) return;
    setPicked(choice);
    setCorrect(ok);
    setDone(true);
    setFlash(ok ? "flash-green" : "flash-rose");
    onResult && onResult(ok);
  };

  // Same card grammar as the other lesson blocks: a colored left accent keys
  // the block type, so a segment scans as a rhythm instead of a wall of
  // identical boxes.
  const wrap = "rounded-lg border border-hair bg-surface2 p-4 " + flash;
  const wrapStyle = { borderLeft: `3px solid ${C.blu}` };

  // ORDER widget uses its own state
  const shuffled = useMemo(() => {
    if (check.type !== "order") return [];
    const arr = check.steps.map((s, i) => ({ s, i }));
    for (let k = arr.length - 1; k > 0; k--) {
      const j = Math.floor(Math.random() * (k + 1));
      [arr[k], arr[j]] = [arr[j], arr[k]];
    }
    return arr;
  }, [check]);
  const [ordered, setOrdered] = useState([]);

  // matchPairs state
  const [matches, setMatches] = useState({});
  const [activeLeft, setActiveLeft] = useState(null);
  const shuffledRights = useMemo(() => {
    if (check.type !== "matchPairs") return [];
    const arr = check.pairs.map((p, i) => ({ text: p.right, i }));
    for (let k = arr.length - 1; k > 0; k--) { const j = Math.floor(Math.random() * (k + 1)); [arr[k], arr[j]] = [arr[j], arr[k]]; }
    return arr;
  }, [check]);

  // slider state
  const [sliderVal, setSliderVal] = useState(check.type === "slider" ? Math.round((check.min + check.max) / 2) : 0);

  const label = {
    predict: "Predict",
    fillBlank: "Fill the blank",
    order: "Order the steps",
    trueFalse: "True or false",
    spotMistake: "Spot the mistake",
    matchPairs: "Match the pairs",
    slider: "Estimate",
    fadedSteps: "Complete the reasoning",
  }[check.type];

  const pickMatch = (rightOriginalIdx) => {
    if (done || activeLeft === null) return;
    const next = { ...matches, [activeLeft]: rightOriginalIdx };
    setActiveLeft(null);
    setMatches(next);
    if (Object.keys(next).length === check.pairs.length) {
      const ok = check.pairs.every((_, li) => next[li] === li);
      finish(ok);
    }
  };

  return (
    <div className={wrap} style={wrapStyle} data-testid="check-widget">
      <div className="mb-3 font-mono text-[11px] uppercase tracking-wider text-blu">{label}</div>

      {check.type === "predict" && (
        <div>
          <p className="mb-3 text-sm text-ink/90">{check.prompt}</p>
          <div className="space-y-2">
            {check.options.map((o, i) => {
              const isAns = i === check.answerIndex;
              const show = done && (isAns || picked === i);
              return (
                <button
                  key={i}
                  disabled={done}
                  data-testid={`predict-opt-${i}`}
                  onClick={() => finish(i === check.answerIndex, i)}
                  className="block w-full rounded-md border px-3 py-2 text-left text-sm transition-colors disabled:cursor-default"
                  style={{
                    borderColor: show ? (isAns ? C.grn : C.ros) : "rgba(255,255,255,0.08)",
                    background: show ? `${isAns ? C.grn : C.ros}14` : "transparent",
                  }}
                >
                  {o}
                </button>
              );
            })}
          </div>
          {done && <Result correct={correct} text={`${check.reveal || ""} ${check.explanation}`.trim()} />}
        </div>
      )}

      {check.type === "fadedSteps" && (
        <div>
          <p className="mb-3 text-sm text-ink/90">{check.prompt}</p>
          <ol className="space-y-1.5">
            {check.steps.map((st, i) => {
              const isBlank = i === check.blankIndex;
              return (
                <li key={i} className="flex items-start gap-2.5 text-sm">
                  <span className="mt-0.5 font-mono text-[10px] text-muted">{String(i + 1).padStart(2, "0")}</span>
                  {isBlank && !done ? (
                    <span className="flex-1 rounded-md border border-dashed border-white/25 px-3 py-1.5 font-mono text-xs text-muted">
                      your step goes here
                    </span>
                  ) : (
                    <span
                      className={`flex-1 ${isBlank ? "rounded-md px-3 py-1.5 anim-fade-up" : "text-ink/90"}`}
                      style={isBlank ? { background: `${C.grn}14`, border: `1px solid ${C.grn}`, color: "var(--ink)" } : undefined}
                    >
                      {st}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
          <div className="mt-3 space-y-2">
            {!done && check.options.map((o, i) => (
              <button
                key={i}
                data-testid={`faded-opt-${i}`}
                onClick={() => finish(i === check.answerIndex, i)}
                className="block w-full rounded-md border border-hair px-3 py-2 text-left text-sm transition-colors hover:bg-white/5"
              >
                {o}
              </button>
            ))}
          </div>
          {done && <Result correct={correct} text={check.explanation} />}
        </div>
      )}

      {check.type === "fillBlank" && (
        <div>
          <p className="mb-3 text-sm text-ink/90">
            {check.sentence.split("___")[0]}
            <span className="mx-1 rounded bg-white/10 px-3 py-0.5 font-mono text-blu">
              {done ? check.options[check.answerIndex] : "___"}
            </span>
            {check.sentence.split("___")[1]}
          </p>
          <div className="flex flex-wrap gap-2">
            {check.options.map((o, i) => {
              const isAns = i === check.answerIndex;
              const show = done && (isAns || picked === i);
              return (
                <button
                  key={i}
                  disabled={done}
                  data-testid={`fill-opt-${i}`}
                  onClick={() => finish(i === check.answerIndex, i)}
                  className="rounded-full border px-3 py-1.5 text-sm transition-colors"
                  style={{
                    borderColor: show ? (isAns ? C.grn : C.ros) : "rgba(255,255,255,0.08)",
                    background: show ? `${isAns ? C.grn : C.ros}14` : "transparent",
                  }}
                >
                  {o}
                </button>
              );
            })}
          </div>
          {done && <Result correct={correct} text={check.explanation} />}
        </div>
      )}

      {check.type === "order" && (
        <div>
          <p className="mb-3 text-sm text-ink/90">{check.prompt || "Tap the steps into the correct order."}</p>
          <div className="mb-3 space-y-2">
            {shuffled.map((item) => {
              const pos = ordered.indexOf(item.i);
              const used = pos !== -1;
              return (
                <button
                  key={item.i}
                  disabled={done || used}
                  data-testid={`order-step-${item.i}`}
                  onClick={() => setOrdered((prev) => [...prev, item.i])}
                  className="flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors disabled:opacity-40"
                  style={{ borderColor: "rgba(255,255,255,0.08)" }}
                >
                  <GripVertical className="h-4 w-4 text-muted" />
                  {used && <span className="font-mono text-xs text-blu">#{pos + 1}</span>}
                  <span>{item.s}</span>
                </button>
              );
            })}
          </div>
          {!done && ordered.length === shuffled.length && (
            <button
              data-testid="order-check-btn"
              onClick={() => finish(ordered.every((v, idx) => v === idx))}
              className="rounded-full bg-blu px-4 py-1.5 text-sm font-medium text-bg"
            >
              Check order
            </button>
          )}
          {!done && ordered.length > 0 && ordered.length < shuffled.length && (
            <button onClick={() => setOrdered([])} className="text-xs text-muted underline">reset</button>
          )}
          {done && <Result correct={correct} text={check.explanation} />}
        </div>
      )}

      {check.type === "trueFalse" && (
        <div>
          <p className="mb-3 text-sm text-ink/90">{check.statement}</p>
          <div className="flex gap-2">
            {[true, false].map((val) => {
              const isAns = val === check.answer;
              const show = done && (isAns || picked === val);
              return (
                <button
                  key={String(val)}
                  disabled={done}
                  data-testid={`tf-${val}`}
                  onClick={() => finish(val === check.answer, val)}
                  className="rounded-full border px-5 py-1.5 text-sm transition-colors"
                  style={{
                    borderColor: show ? (isAns ? C.grn : C.ros) : "rgba(255,255,255,0.08)",
                    background: show ? `${isAns ? C.grn : C.ros}14` : "transparent",
                  }}
                >
                  {val ? "True" : "False"}
                </button>
              );
            })}
          </div>
          {done && <Result correct={correct} text={check.explanation} />}
        </div>
      )}

      {check.type === "spotMistake" && (
        <div>
          <p className="mb-3 rounded-md border border-hair bg-surface p-3 text-sm text-ink/90">{check.text}</p>
          <p className="mb-2 text-xs text-muted">Is this statement correct?</p>
          <div className="flex gap-2">
            <button
              disabled={done}
              data-testid="spot-correct"
              onClick={() => finish(false, "correct")}
              className="rounded-full border px-4 py-1.5 text-sm"
              style={{ borderColor: done && picked === "correct" ? C.ros : "rgba(255,255,255,0.08)" }}
            >
              Looks correct
            </button>
            <button
              disabled={done}
              data-testid="spot-wrong"
              onClick={() => finish(true, "wrong")}
              className="rounded-full border px-4 py-1.5 text-sm"
              style={{ borderColor: done && picked === "wrong" ? C.grn : "rgba(255,255,255,0.08)" }}
            >
              Something's wrong
            </button>
          </div>
          {done && <Result correct={correct} text={`The error: ${check.mistake}. ${check.correction}`} />}
        </div>
      )}

      {check.type === "matchPairs" && (
        <div>
          <p className="mb-3 text-sm text-ink/90">{check.prompt || "Tap a term, then tap its match."}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              {check.pairs.map((p, li) => {
                const matched = matches[li] !== undefined;
                const activeSel = activeLeft === li;
                const ok = done && matches[li] === li;
                return (
                  <button
                    key={li}
                    disabled={done || matched}
                    data-testid={`match-left-${li}`}
                    onClick={() => setActiveLeft(li)}
                    className="press block w-full rounded-md border px-3 py-2 text-left text-sm disabled:opacity-70"
                    style={{ borderColor: done ? (ok ? C.grn : C.ros) : activeSel ? C.blu : "rgba(255,255,255,0.08)", background: activeSel ? `${C.blu}18` : "transparent" }}
                  >
                    {p.left}
                  </button>
                );
              })}
            </div>
            <div className="space-y-2">
              {shuffledRights.map((r) => {
                const used = Object.values(matches).includes(r.i);
                return (
                  <button
                    key={r.i}
                    disabled={done || used}
                    data-testid={`match-right-${r.i}`}
                    onClick={() => pickMatch(r.i)}
                    className="press block w-full rounded-md border px-3 py-2 text-left text-sm disabled:opacity-40"
                    style={{ borderColor: "rgba(255,255,255,0.08)" }}
                  >
                    {r.text}
                  </button>
                );
              })}
            </div>
          </div>
          {done && <Result correct={correct} text={check.explanation} />}
        </div>
      )}

      {check.type === "slider" && (
        <div>
          <p className="mb-3 text-sm text-ink/90">{check.prompt}</p>
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-muted">{withUnit(check.min, check.unit)}</span>
            <input
              type="range"
              min={check.min}
              max={check.max}
              value={sliderVal}
              disabled={done}
              data-testid="slider-input"
              onChange={(e) => setSliderVal(Number(e.target.value))}
              className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-white/10 accent-blu"
              style={{ accentColor: C.blu }}
            />
            <span className="font-mono text-xs text-muted">{withUnit(check.max, check.unit)}</span>
          </div>
          <div className="mt-2 text-center font-mono text-lg" style={{ color: C.blu }}>{withUnit(sliderVal, check.unit)}</div>
          {!done && (
            <button data-testid="slider-check-btn" onClick={() => finish(Math.abs(sliderVal - check.answer) <= check.tolerance)} className="press mt-2 rounded-full px-4 py-1.5 text-sm font-medium text-bg" style={{ background: C.blu }}>
              Lock in estimate
            </button>
          )}
          {done && <Result correct={correct} text={`Answer: ${withUnit(check.answer, check.unit)}. ${check.reveal || check.explanation}`} />}
        </div>
      )}
    </div>
  );
}
